import React, { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { authFetch } from "../utils/authFetch";
import { BASE_URL } from "../utils/config";
import GoogleMapDrawCircle from "../components/GoogleMapDrawCircle";
import { useMemo } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useFlow } from "../context/FlowContext";
import { useTranslation } from "react-i18next";
import styles from "./SearchProperty.module.css";
import NewOffersSwiper from "../components/NewOffersSwiper";
import { prepareCriteriaForSave, loadCriteriaFromBackend, getDisplayValue, isCriteriaSelected, getIdentifier } from "../utils/criteriaHelpers";

const removeApostrophes = (text) => {
  if (typeof text !== "string") {
    return text;
  }
  return text.replace(/'/g, "");
};

const formatWithApostrophe = (value) => {
  if (!value && value !== 0) return "";
  const numValue =
    typeof value === "string" ? parseFloat(value.replace(/'/g, "")) : value;
  if (isNaN(numValue)) return value;
  const digitsOnly = numValue.toString().replace(/[^\d]/g, "");
  return digitsOnly.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
};

// Helper function to capitalize the first letter of a string
const capitalizeFirstLetter = (string) => {
  if (!string) return "";
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const ensureArray = (value) => (Array.isArray(value) ? value : []);
const SearchProperty = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { isAuthenticated, currentUser } = useAuth();
  const { id } = useParams(); // Get the ID from the URL
  const [searchParams] = useSearchParams(); // Get URL search parameters
  const isEditMode = !!id;
  const { setFlow } = useFlow();
  const [step, setStep] = useState(1);
  const [activeTab, setActiveTab] = useState(1);
  const [maxAllowedStep, setMaxAllowedStep] = useState(1);
  const [showAddressDropdown, setShowAddressDropdown] = useState(false);
  const [activeZipField, setActiveZipField] = useState(null);
  const [showZipRangeInputFields, setShowZipRangeInputFields] = useState(false);
  const [zipRangeMinMax, setZipRangeMinMax] = useState({
    minZipCode: "",
    maxZipCode: "",
  });
  const [zipSuggestions, setZipSuggestions] = useState([]);
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [isAddingNewLocation, setIsAddingNewLocation] = useState(true);
  const [demands, setDemands] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);
  const addressInputRef = useRef(null);

  const [formData, setFormData] = useState({
    offeringType: "purchase",
    propertyType: "house/villa",
    address: "",
    price: "",
    bedrooms: "",
    squareFootage: "",
    units: "",
    index: "",
    criteria: Array(40).fill(false),
    images: [],
    areasFromShape: [],
    mustCriteria: {},
    shouldCriteria: {},
    matchingLevel: {
      percentage: "70",
      source_type: "all",
    },
    zipFrom: "",
    zipTo: "",
    country: "",
    city: "",
    zipcode: "",
  });

  useEffect(() => {
    setFlow("demand");
  }, [setFlow]);

  // Auto-select offering type based on URL parameter (from MySearch tab selection)
  useEffect(() => {
    if (!isEditMode) {
      const offeringTypeParam = searchParams.get("offeringType");
      if (offeringTypeParam === "purchase" || offeringTypeParam === "rental") {
        setFormData((prev) => ({
          ...prev,
          offeringType: offeringTypeParam,
        }));
      }
    }
  }, [searchParams, isEditMode]);

  useEffect(() => {
    const fetchDemands = async () => {
      try {
        const response = await authFetch(
          `${BASE_URL}/property/latest/demands?limit=30`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch demands");
        }

        const contentType = response.headers.get("content-type");
        if (contentType && contentType.indexOf("application/json") !== -1) {
          const data = await response.json();
          console.log("📊 Fetched demands data:", data);
          console.log("📊 Number of demands:", data?.length || 0);
          // Ensure we always set an array, even if API returns null/undefined
          setDemands(Array.isArray(data) ? data : []);
        } else {
          const text = await response.text();
          throw new Error(
            `Server returned a non-JSON response. Check your API route. Response was: "${text.substring(
              0,
              50
            )}..."`
          );
        }
      } catch (err) {
        setError(err.message);
        console.error("Error fetching demands:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDemands();

    // const intervalId = setInterval(fetchDemands, 60000);

    return () => {
      // Polling disabled to prevent property changes during animation
      // clearInterval(intervalId);
    };
  }, []);

  const [loadingInitialData, setLoadingInitialData] = useState(isEditMode);
  useEffect(() => {
    if (isEditMode) {
      const fetchPropertyData = async () => {
        try {
          setLoadingInitialData(true);
          const response = await authFetch(`${BASE_URL}/property/${id}`);
          const result = await response.json();

          if (response.ok && result.data) {
            const propertyData = result.data;
            console.log("Fetched property data for edit:", propertyData);
            console.log("PropertyAddresses array:", propertyData.PropertyAddresses);
            console.log("Number of PropertyAddresses:", propertyData.PropertyAddresses?.length || 0);

            const populatedLocations = [];
            const populatedAreasFromShape = [];

            if (!propertyData.PropertyAddresses || propertyData.PropertyAddresses.length === 0) {
              console.warn("⚠️ No PropertyAddresses found for this property!");
            }

            propertyData.PropertyAddresses?.forEach((address, index) => {
              console.log(`Processing address ${index}:`, address);
              const commonLocationData = {
                countryId: address.countryId,
                countryISO: address.countryISO,
                countryName: address.countryName,
              };

              // NEW: Handle consolidated format (multiple cities in zipList object)
              if (
                address.zipList &&
                typeof address.zipList === 'object' &&
                !address.zipList.zipRange &&
                Object.keys(address.zipList).length > 0
              ) {
                console.log("🆕 Detected consolidated address format!");
                console.log("zipList:", address.zipList);

                // Extract all city/ZIP combinations from consolidated format
                Object.entries(address.zipList).forEach(([city, zips]) => {
                  if (Array.isArray(zips)) {
                    zips.forEach(zip => {
                      const areaData = {
                        ...commonLocationData,
                        country: address.countryName,
                        city: city,
                        zip: zip,
                      };
                      const mapAreaLocation = {
                        display: `${city}, ${zip}`,
                        type: "map-area",
                        originalData: areaData,
                      };
                      console.log("✅ Added consolidated location:", mapAreaLocation);
                      populatedLocations.push(mapAreaLocation);
                      populatedAreasFromShape.push(areaData);
                    });
                  }
                });
              }
              // Handle ZIP range format
              else if (
                address.zipList &&
                address.zipList.zipRange &&
                address.zipList.zipRange.length === 2
              ) {
                const zipRangeLocation = {
                  display: `Zip: ${address.zipList.zipRange[0]} - ${address.zipList.zipRange[1]}`,
                  type: "zip-range",
                  originalData: {
                    ...commonLocationData,
                    zipFrom: address.zipList.zipRange[0],
                    zipTo: address.zipList.zipRange[1],
                  },
                };
                console.log("✅ Added zip-range location:", zipRangeLocation);
                populatedLocations.push(zipRangeLocation);
              }
              // Handle single city/ZIP format (old format or single location)
              else if (
                address.city &&
                address.zipCode &&
                address.countryName
              ) {
                const areaData = {
                  ...commonLocationData,
                  country: address.countryName,
                  city: address.city,
                  zip: address.zipCode,
                };
                const mapAreaLocation = {
                  display: `${address.city}, ${address.zipCode}`,
                  type: "map-area",
                  originalData: areaData,
                };
                console.log("✅ Added map-area location:", mapAreaLocation);
                populatedLocations.push(mapAreaLocation);
                populatedAreasFromShape.push(areaData);
              } else {
                console.warn("⚠️ Address didn't match any pattern:", {
                  hasZipList: !!address.zipList,
                  zipListType: typeof address.zipList,
                  hasZipRange: !!address.zipList?.zipRange,
                  zipRangeLength: address.zipList?.zipRange?.length,
                  hasCity: !!address.city,
                  hasZipCode: !!address.zipCode,
                  hasCountryName: !!address.countryName
                });
              }
            });

            // CRITICAL FIX: Backend returns DemandPropertyCriterium (SINGULAR), not DemandPropertyCriteria (plural)
            const matchedLevelsFromApi = {};
            const levels =
              propertyData?.DemandPropertyCriterium?.matchingLevel || [];

            console.log("levels?????????????", levels);
            console.log("✅ DemandPropertyCriterium exists (CORRECT - singular)?", !!propertyData?.DemandPropertyCriterium);
            console.log("❌ DemandPropertyCriteria exists (WRONG - plural)?", !!propertyData?.DemandPropertyCriteria);

            // levels.forEach((level) => {
            //   if (level.name && level.options?.length > 0) {
            //     const name = level.name;
            //     const label = level.options[0].label?.trim();
            //     console.log("level name and label", name, label);
            //     // Normalize label
            //     if (name === "percentage") {
            //       if (level.options[0].value === "70") {
            //         matchedLevelsFromApi.percentage = ">=70%";
            //       }
            //     } else if (name === "source_type") {
            //       matchedLevelsFromApi.source_type = label;
            //     }
            //   }
            // });
            levels.forEach((level) => {
              if (level.name && level.options?.length > 0) {
                const name = level.name;
                const option = level.options[0];

                matchedLevelsFromApi[name] = option.value; // ✅ store value not label
              }
            });

            console.log("🗺️ Populated locations for edit mode:", populatedLocations);
            console.log("📍 Number of locations:", populatedLocations.length);

            // DEBUG ALERT: Show what we got from backend
            if (populatedLocations.length === 0) {
              console.error("❌ PROBLEM: No locations were populated from PropertyAddresses!");
              console.error("PropertyAddresses from backend:", propertyData.PropertyAddresses);
            }

            // Store raw criteria from backend - will be normalized once criteriaGroups loads
            const savedMustCriteria = propertyData.DemandPropertyCriterium?.mustCriteria || {};
            const savedShouldCriteria = propertyData.DemandPropertyCriterium?.shouldCriteria || {};

            console.log("📥 Raw criteria from backend:", {
              mustCriteria: savedMustCriteria,
              shouldCriteria: savedShouldCriteria
            });

            // Store original criteria (identifier-based) for language switching in edit mode
            setOriginalMustCriteria(savedMustCriteria);
            setOriginalShouldCriteria(savedShouldCriteria);

            setSelectedLocations(populatedLocations);
            setFormData((prev) => ({
              ...prev,
              offeringType: propertyData.offeringType || "",
              propertyType: propertyData.propertyType || "",
              price: propertyData.price
                ? formatWithApostrophe(propertyData.price)
                : "",
              bedrooms: propertyData.bedrooms || "",
              units: propertyData.units || "",
              index: propertyData.index !== undefined ? String(propertyData.index) : "",
              squareFootage: propertyData.squareMeters
                ? formatWithApostrophe(propertyData.squareMeters)
                : "",
              areasFromShape: populatedAreasFromShape,
              mustCriteria: savedMustCriteria,  // Store raw first, will normalize in useEffect
              shouldCriteria: savedShouldCriteria,  // Store raw first, will normalize in useEffect
              matchingLevel: matchedLevelsFromApi,
            }));

            setIsAddingNewLocation(populatedLocations.length === 0);
          } else {
            toast.error(
              result.message || "Failed to fetch property details for editing."
            );
            navigate("/");
          }
        } catch (error) {
          console.error("Error fetching property data:", error);
          toast.error("An error occurred while loading property data.");
          navigate("/");
        } finally {
          setLoadingInitialData(false);
        }
      };
      fetchPropertyData();
    }
  }, [
    id,
    isEditMode,
    navigate,
    setFlow,
    BASE_URL,
    formatWithApostrophe,
    authFetch,
    setFormData,
    setSelectedLocations,
  ]);

  // Debug: Log when criteria changes in formData
  useEffect(() => {
    if (isEditMode && Object.keys(formData.mustCriteria).length > 0) {
      console.log("📝 formData.mustCriteria updated:", formData.mustCriteria);
      console.log("📝 formData.shouldCriteria updated:", formData.shouldCriteria);
    }
  }, [formData.mustCriteria, formData.shouldCriteria, isEditMode]);

  // In edit mode, allow access to all steps immediately
  useEffect(() => {
    if (isEditMode) {
      setMaxAllowedStep(3); // Enable all tabs in edit mode
    }
  }, [isEditMode]);

  const handleCheckboxChangeShould = (groupName, option) => {
    const currentGroup = ensureArray(formData.shouldCriteria[groupName] || []);
    const isSelected = currentGroup.includes(option);

    // Calculate total excluding criteria that are also in must-have
    let totalSelected = 0;
    Object.entries(formData.shouldCriteria || {}).forEach(([grpName, values]) => {
      const mustValues = formData.mustCriteria?.[grpName] || [];
      const valuesArray = Array.isArray(values) ? values : [values];

      valuesArray.forEach(value => {
        if (!mustValues.includes(value)) {
          totalSelected++;
        }
      });
    });

    if (!isSelected && totalSelected === 9) {
      toast.info(t("you_have_selected_max_nice_options"));
    }

    if (!isSelected && totalSelected >= 10) {
      return;
    }

    const updatedGroup = isSelected
      ? currentGroup.filter((item) => item !== option)
      : [...currentGroup, option];

    setFormData((prev) => ({
      ...prev,
      shouldCriteria: {
        ...prev.shouldCriteria,
        [groupName]: updatedGroup,
      },
    }));
  };

  const calculateTotalSelected = (criteriaObject) => {
    return Object.values(criteriaObject || {}).reduce(
      (sum, group) => sum + group.length,
      0
    );
  };

  const handleCheckboxChange = (groupName, option) => {
    const currentGroup = ensureArray(formData.mustCriteria[groupName] || []);
    const isSelected = currentGroup.includes(option);
    const totalSelected = calculateTotalSelected(formData.mustCriteria);

    if (!isSelected && totalSelected === 4) {
      toast.info(t("you_have_selected_max_must_options"));
    }

    if (!isSelected && totalSelected >= 5) {
      return; // Don't allow more
    }

    const updatedGroup = isSelected
      ? currentGroup.filter((item) => item !== option)
      : [...currentGroup, option];

    setFormData((prev) => ({
      ...prev,
      mustCriteria: {
        ...prev.mustCriteria,
        [groupName]: updatedGroup,
      },
    }));
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");
    if (token && user) {
      console.log("SearchProperty: User is already logged in.");
    } else {
      console.log(
        "SearchProperty: User is not logged in. Will prompt for login/registration."
      );
    }
  }, []);

  const [criteriaGroups, setCriteriaGroups] = useState([]);
  const [loadingCriteria, setLoadingCriteria] = useState(true);
  const [matchingLevelOptions, setMatchingLevelOptions] = useState({
    percentage: [],
    source_type: [],
  });
  const [originalMustCriteria, setOriginalMustCriteria] = useState(null); // Store identifier-based criteria for language switching
  const [originalShouldCriteria, setOriginalShouldCriteria] = useState(null);

  useEffect(() => {
    const fetchCriteriaOptions = async () => {
      try {
        // Access the current language code from i18n.language
        const langCode = i18n.language.split("-")[0]; // "en-US" → "en"

        // Use the dynamic language code in your API call
        const response = await authFetch(
          // `${BASE_URL}/property/criteria-options/${
          //   i18n.language == "en-US" ? "en" : i18n.language
          // }`
          `${BASE_URL}/property/criteria-options/${langCode}`
        );
        const data = await response.json();
        if (response.ok) {
          // Sort criteria groups by sortOrder
          const sortedCriteria = (data?.data || []).sort((a, b) => {
            return (a.sortOrder || 0) - (b.sortOrder || 0);
          });
          setCriteriaGroups(sortedCriteria);
        } else {
          console.error("Failed to fetch criteria options:", data);
        }
      } catch (error) {
        console.error("Error fetching criteria options:", error);
      } finally {
        setLoadingCriteria(false);
      }
    };
    fetchCriteriaOptions();
  }, [i18n.language]); // Removed 'step' dependency to avoid unnecessary re-fetches

  // Normalize criteria once criteriaGroups are loaded (for edit mode or language change)
  // ONLY normalize from originalCriteria (identifier-based), NEVER from formData
  useEffect(() => {
    if (criteriaGroups.length > 0 && isEditMode && (originalMustCriteria || originalShouldCriteria)) {
      console.log("🔄 Normalizing demand criteria to current language...");
      console.log("  - Original must criteria (identifier keys):", originalMustCriteria);
      console.log("  - Original should criteria (identifier keys):", originalShouldCriteria);

      // Convert identifier keys to groupName keys, keep identifier values
      const normalizedMust = loadCriteriaFromBackend(originalMustCriteria || {}, criteriaGroups);
      const normalizedShould = loadCriteriaFromBackend(originalShouldCriteria || {}, criteriaGroups);

      console.log("✅ Criteria normalized:", {
        mustCriteria: normalizedMust,
        shouldCriteria: normalizedShould
      });

      setFormData(prev => ({
        ...prev,
        mustCriteria: normalizedMust,
        shouldCriteria: normalizedShould
      }));
    }
  }, [criteriaGroups, isEditMode, originalMustCriteria, originalShouldCriteria]); // Run when criteriaGroups loads or changes

  useEffect(() => {
    const fetchMatchingLevels = async () => {
      try {
        const currentLanguage = i18n.language == "en-US" ? "en" : i18n.language;
        const response = await authFetch(
          `${BASE_URL}/property/matching-level/${currentLanguage}`
        );
        const result = await response.json();
        if (response.ok) {
          const grouped = { percentage: [], source_type: [] };
          for (const group of result.data) {
            if (group.name === "percentage") grouped.percentage = group.options;
            else if (group.name === "source_type")
              grouped.source_type = group.options;
          }
          setMatchingLevelOptions(grouped);
        } else {
          console.error(
            "❌ Failed to fetch matching level options:",
            result.message
          );
        }
      } catch (error) {
        console.error("❌ Error fetching matching level options:", error);
      }
    };
    fetchMatchingLevels();
  }, [i18n.language]);

  useEffect(() => {
    const fetchZipCodeDefaults = async () => {
      try {
        const response = await authFetch(`${BASE_URL}/country/zipcodes/ch`);
        const result = await response.json();
        if (response.ok && result.data) {
          setZipRangeMinMax({
            minZipCode: result.data.minZipCode,
            maxZipCode: result.data.maxZipCode,
          });
        } else {
          console.error(
            "❌ Failed to fetch zip code defaults:",
            result.message
          );
        }
      } catch (error) {
        console.error("❌ Error fetching zip code defaults:", error);
      }
    };
    fetchZipCodeDefaults();
  }, []);

  const totalMustSelected = useMemo(() => {
    return Object.values(formData.mustCriteria || {}).flat().length;
  }, [formData.mustCriteria]);

  const totalNiceSelected = useMemo(() => {
    if (!formData.shouldCriteria) return 0;

    let count = 0;
    Object.entries(formData.shouldCriteria).forEach(([groupName, values]) => {
      const mustValues = formData.mustCriteria?.[groupName] || [];
      const valuesArray = Array.isArray(values) ? values : [values];

      // Only count values that are NOT also in mustCriteria
      valuesArray.forEach(value => {
        if (!mustValues.includes(value)) {
          count++;
        }
      });
    });

    return count;
  }, [formData.shouldCriteria, formData.mustCriteria]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === "price" || name === "squareFootage" || name === "units") {
      newValue = formatWithApostrophe(value);
    }
    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const allAvailableZips = useMemo(() => {
    const zips = [];
    const min = parseInt(zipRangeMinMax.minZipCode, 10);
    const max = parseInt(zipRangeMinMax.maxZipCode, 10);

    if (!isNaN(min) && !isNaN(max) && min <= max) {
      for (let i = min; i <= max; i++) {
        zips.push(i.toString());
      }
    }
    return zips;
  }, [zipRangeMinMax.minZipCode, zipRangeMinMax.maxZipCode]);

  const getZipSuggestions = (input, count = 5, order = "ascending") => {
    if (!input || input.length === 0) {
      return [];
    }

    const filteredZips = allAvailableZips.filter((zip) =>
      zip.startsWith(input)
    );

    if (order === "descending") {
      filteredZips.sort((a, b) => parseInt(b, 10) - parseInt(a, 10));
    } else {
      filteredZips.sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
    }

    return filteredZips.slice(0, count);
  };

  const handleChangeOfZip = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      const suggestionOrder = name === "zipTo" ? "descending" : "ascending";

      const currentSuggestions = getZipSuggestions(value, 5, suggestionOrder);

      if (value.length === 4 && !isNaN(parseInt(value, 10))) {
        setZipSuggestions([]);
      } else {
        setZipSuggestions(currentSuggestions);
      }

      return updated;
    });
  };
  const handleZipInputFocus = (fieldName) => {
    setActiveZipField(fieldName);
    const value = fieldName === "zipFrom" ? formData.zipFrom : formData.zipTo;
    const suggestionOrder = fieldName === "zipTo" ? "descending" : "ascending";
    if (!(value.length === 4 && !isNaN(parseInt(value, 10)))) {
      setZipSuggestions(getZipSuggestions(value, 5, suggestionOrder));
    }
  };
  const handleZipInputBlur = (e) => {
    if (
      e.relatedTarget &&
      e.relatedTarget.closest(`.${styles.suggestionItem}`)
    ) {
      return;
    }

    setActiveZipField(null);
    setZipSuggestions([]);
  };
  const handleZipSuggestionClick = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setZipSuggestions([]);
  };

  const handleRadioChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleMatchingLevelRadioChange = (type, selectedValue) => {
    // Renamed 'label' to 'selectedValue' for clarity
    setFormData((prevData) => ({
      ...prevData,
      matchingLevel: {
        ...prevData.matchingLevel,
        [type]: selectedValue, // This will now set the value (e.g., "all", "private", "agency")
      },
    }));
  };

  const nextStep = () => {
    setStep((prev) => {
      const newStep = prev + 1;
      // Update maxAllowedStep to ensure user can click back to the current step
      // and has access to all previously visited steps.
      setMaxAllowedStep((prevMax) => Math.max(prevMax, newStep));
      return newStep;
    });
    setActiveTab((prev) => prev + 1); // Assuming activeTab is directly tied to step
  };

  const handleNextFromMustCriteria = () => {
    const totalMustSelected = calculateTotalSelected(formData.mustCriteria);
    if (totalMustSelected < 3) {
      toast.info(t("minimum_criteria_required"));
      return;
    }
    nextStep();
  };

  const prevStep = () => {
    setStep((prev) => prev - 1);
    setActiveTab((prev) => prev - 1);
  };

  const handleDeleteLocation = (indexToRemove, type) => {
    setSelectedLocations((prev) => {
      const updatedSelectedLocations = prev.filter(
        (_, i) => i !== indexToRemove
      );

      if (type === "map-area") {
        setFormData((p) => {
          const updatedAreasFromShape = p.areasFromShape.filter(
            (_, i) => i !== indexToRemove
          );
          return {
            ...p,
            areasFromShape: updatedAreasFromShape,
          };
        });
      } else if (type === "zip-range") {
        setFormData((p) => ({
          ...p,
          zipFrom: "",
          zipTo: "",
        }));
      } else if (type === "address") {
        setFormData((p) => ({
          ...p,
          address: "",
          country: "",
          city: "",
          zipcode: "",
        }));
      }
      if (updatedSelectedLocations.length === 0) {
        setIsAddingNewLocation(true);
        setFormData((p) => ({
          ...p,
          areasFromShape: [],
          zipFrom: "",
          zipTo: "",
        }));
      }

      return updatedSelectedLocations;
    });
  };

  //use in map draw
  const handleDrawMapClick = () => {
    setShowAddressDropdown(false);
    setShowMapModal(true);
    setShowZipRangeInputFields(false);
  };

  const handleZipRangeSelect = () => {
    setShowZipRangeInputFields((prev) => !prev);
    setFormData((prev) => ({
      ...prev,
    }));
  };

  const handleSubmitZipRange = () => {
    if (!formData.zipFrom || !formData.zipTo) {
      toast.error("Please enter both 'From' and 'To' zip codes.");
      return;
    }
    if (Number(formData.zipFrom) > Number(formData.zipTo)) {
      toast.error("'From' zip code cannot be greater than 'To' zip code.");
      return;
    }

    setSelectedLocations((prev) => [
      ...prev,
      {
        display: `Zip: ${formData.zipFrom} - ${formData.zipTo}`,
        type: "zip-range",
        originalData: { zipFrom: formData.zipFrom, zipTo: formData.zipTo },
      },
    ]);

    setFormData((prev) => ({
      ...prev,
      zipFrom: "",
      zipTo: "",
    }));

    toast.success(
      `Zip Range: ${formData.zipFrom} - ${formData.zipTo} added successfully!`
    );
    setShowZipRangeInputFields(false);
    setIsAddingNewLocation(false);
  };

  //use in map draw
  const handleAddressFieldClick = () => {
    setShowAddressDropdown((prev) => !prev);
    setShowZipRangeInputFields(false);
    setZipSuggestions([]);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        addressInputRef.current &&
        !addressInputRef.current.contains(event.target)
      ) {
        setShowAddressDropdown(false);
        setShowZipRangeInputFields(false);
        setZipSuggestions([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // const handleMapDrawDone = (newAreas) => {
  //   console.log('Extracted areas from shape:', newAreas);
  //   setSelectedLocations(prev => [
  //     ...prev,
  //     ...newAreas.map(area => ({ type: 'drawn', display: area.city || 'Drawn Area', data: area }))
  //   ]);
  //   setFormData(prev => ({
  //     ...prev,
  //     areasFromShape: [...prev.areasFromShape, ...newAreas],
  //     zipFrom: '',
  //     zipTo: '',
  //     address: '',
  //   }));
  //   setShowMapModal(false);
  //   setIsAddingNewLocation(false);
  //   toast.success("Area(s) drawn successfully!");
  // };
  const displayStringToKeyMap = {
    "House/Villa": "house/villa", // <--- This must exactly match the DB enum value
    Apartment: "apartment",
    Multifamily: "multifamily",
    Plot: "plot",
  };

  const handleNextStep = async () => {
    console.log("--- FormData before next step ---");
    console.log("Current formData:", formData);
    console.log("Selected Locations (UI Tags):", selectedLocations);
    console.log("---------------------------------");

    if (!formData.offeringType || !formData.propertyType || !formData.price) {
      toast.error("Please fill in Offering Type, Property Type, and Price.");
      return;
    }

    if (selectedLocations.length === 0) {
      toast.error(
        "Please select a location using 'Draw Your Map' or 'Zip Ranges' or by entering an address."
      );
      return;
    }

    if (isEditMode) {
      nextStep();
      console.log(
        "In Edit Mode, skipping API call for Step 1. Proceeding to next step."
      );
      return; // Exit the function
    }

    const addressesToSend = [];

    selectedLocations.forEach((location) => {
      if (location.type === "zip-range" && location.originalData) {
        const countryName = "Switzerland";
        const countryISO = "CH";
        addressesToSend.push({
          countryName: countryName,
          countryISO: countryISO,
          city: "",
          zipCode: "",
          fullAddress: `Zip Range: ${location.originalData.zipFrom} - ${location.originalData.zipTo} (${countryName})`,
          zipList: {
            zipRange: [
              location.originalData.zipFrom,
              location.originalData.zipTo,
            ],
          },
        });
      } else if (location.type === "map-area" && location.originalData) {
        const area = location.originalData;
        const countryISO = area.countryISO;
        addressesToSend.push({
          countryName: area.country,
          countryISO: countryISO,
          city: area.city,
          zipCode: area.zip,
          fullAddress: `${area.city}, ${area.zip}, ${area.country}`,
          zipList: {
            [area.city]: [area.zip],
          },
        });
      } else if (location.type === "address" && location.originalData) {
        const singleAddress = location.originalData;
        const countryISO = singleAddress.countryISO;
        addressesToSend.push({
          countryName: singleAddress.country,
          countryISO: countryISO,
          city: singleAddress.city,
          zipCode: singleAddress.zipcode,
          fullAddress: singleAddress.fullAddress,
          zipList: {},
        });
      }
    });

    const payload = {
      offeringType: formData.offeringType,
      propertyType: formData.propertyType,
      price: Number(removeApostrophes(formData.price)) || 0,
      bedrooms: Number(formData.bedrooms) || 0,
      units: Number(formData.units) || 0,
      index: formData.index ? Number(formData.index) : null,
      squareMeters: Number(removeApostrophes(formData.squareFootage)) || 0,
      listType: "demand",
      addresses: addressesToSend,
    };

    console.log("📦 Sending Step 1 payload to API:", payload);

    try {
      const response = await authFetch(`${BASE_URL}/property/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok) {
        const propertyId = result.data.id;
        localStorage.setItem("propertyId", propertyId);
        console.log(
          "✅ Property created, saved ID to localStorage:",
          propertyId
        );

        // Trigger matching recalculation after property creation
        try {
          const matchingResponse = await authFetch(
            `${BASE_URL}/property/matching/recalculate`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                propertyId: propertyId,
              }),
            }
          );
          if (matchingResponse.ok) {
            console.log(
              "✅ Matching algorithm triggered successfully after property creation"
            );
          } else {
            console.warn(
              "⚠️ Matching recalculation request failed, but property was created"
            );
          }
        } catch (matchingError) {
          // Don't fail the whole operation if matching trigger fails
          console.warn(
            "⚠️ Error triggering matching recalculation:",
            matchingError
          );
        }

        nextStep();
      } else {
        console.error("❌ API error creating property:", result.message);
        toast.error(
          result.message || "Failed to create property. Please try again."
        );
      }
    } catch (error) {
      console.error("❌ Error during property creation:", error);
      toast.error("An unexpected error occurred. Please try again.");
    }
  };

  const handleSubmit = async () => {
    // Validate Step 1 required fields
    if (!formData.price) {
      toast.info(t("missing_price_toast"));
      setStep(1);
      setActiveTab(1);
      return;
    }
    if (selectedLocations.length === 0) {
      toast.error(
        "Please select a location using 'Draw Your Map' or by entering an address."
      );
      setStep(1);
      setActiveTab(1);
      return;
    }
    if (
      formData.propertyType !== "plot" &&
      formData.propertyType !== "multifamily" &&
      !formData.bedrooms
    ) {
      toast.info(t("missing_bedrooms_toast"));
      setStep(1);
      setActiveTab(1);
      return;
    }
    if (formData.propertyType === "plot" && (formData.index === '' || formData.index === null || formData.index === undefined)) {
      toast.info(t("missing_index_toast"));
      setStep(1);
      setActiveTab(1);
      return;
    }
    if (!formData.squareFootage) {
      toast.info(t("missing_squareFootage_toast"));
      setStep(1);
      setActiveTab(1);
      return;
    }

    // Validate Step 2 - must criteria >= 3
    const totalMustSelectedCount = calculateTotalSelected(formData.mustCriteria);
    if (totalMustSelectedCount < 3) {
      toast.info(t("minimum_criteria_required"));
      setStep(2);
      setActiveTab(2);
      return;
    }

    // Validate Step 3 - nice-to-have criteria >= 3
    const totalNiceSelectedCount = calculateTotalSelected(formData.shouldCriteria);
    if (totalNiceSelectedCount < 3) {
      toast.info(t("minimum_criteria_required"));
      return;
    }

    const propertyId = isEditMode ? id : localStorage.getItem("propertyId");
    console.log("propertyId from localStorage:", propertyId);
    if (!propertyId) {
      console.error(
        "❌ Property ID not found in localStorage. Cannot submit criteria."
      );
      return;
    }

    // Convert criteria from translated groupNames to identifiers for backend
    console.log("📤 Preparing criteria for save...");
    console.log("  - Current formData.mustCriteria:", formData.mustCriteria);
    console.log("  - Current formData.shouldCriteria:", formData.shouldCriteria);
    console.log("  - Available criteriaGroups:", criteriaGroups);

    const transformedMustCriteria = prepareCriteriaForSave(formData.mustCriteria, criteriaGroups);
    const transformedShouldCriteria = prepareCriteriaForSave(formData.shouldCriteria, criteriaGroups);

    console.log("✅ Criteria converted to identifiers:");
    console.log("  - Must criteria (identifiers):", transformedMustCriteria);
    console.log("  - Should criteria (identifiers):", transformedShouldCriteria);

    const apiMatchingLevel = [];
    if (formData.matchingLevel.percentage) {
      // Find the option whose 'value' matches the stored formData.matchingLevel.percentage
      const selectedPercentageOption = matchingLevelOptions.percentage.find(
        (opt) => opt.value === formData.matchingLevel.percentage
      );
      if (selectedPercentageOption) {
        apiMatchingLevel.push({
          id: selectedPercentageOption.id,
          name: "percentage",
          options: [
            {
              id: selectedPercentageOption.id,
              label: selectedPercentageOption.label, // Send the original label (e.g., ">=70%")
              value: selectedPercentageOption.value,
              comparator_type: selectedPercentageOption.comparator_type,
            },
          ],
        });
      }
    }
    if (formData.matchingLevel.source_type) {
      // Find the option whose 'value' matches the stored formData.matchingLevel.source_type
      const selectedSourceTypeOption = matchingLevelOptions.source_type.find(
        (opt) => opt.value === formData.matchingLevel.source_type
      );
      if (selectedSourceTypeOption) {
        apiMatchingLevel.push({
          id: selectedSourceTypeOption.id,
          name: "source_type",
          options: [
            {
              id: selectedSourceTypeOption.id,
              label: selectedSourceTypeOption.label, // Send the translated label from the current options
              value: selectedSourceTypeOption.value,
              comparator_type: selectedSourceTypeOption.comparator_type,
            },
          ],
        });
      }
    }

    if (!isEditMode) {
      const criteriaPayload = {
        propertyId: propertyId, // Link to the newly created property
        mustCriteria: transformedMustCriteria,
        shouldCriteria: transformedShouldCriteria,
        matchingLevel: apiMatchingLevel,
      };

      console.log(
        "📦 Sending final criteria payload (NEW CREATE) to API:",
        criteriaPayload
      );

      try {
        const response = await authFetch(
          `${BASE_URL}/property/demand-criteria`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(criteriaPayload),
          }
        );
        const result = await response.json();

        if (response.ok) {
          console.log("✅ New demand criteria submitted successfully:", result);
          toast.success(
            "Your search criteria have been successfully submitted!"
          );
          // Clear localStorage after successful submission
          navigate(
            isAuthenticated && currentUser && currentUser.userType === "demand"
              ? "/"
              : "/auth/login?flowType=demand"
          );
        } else {
          console.error(
            "❌ API error submitting new criteria:",
            result.message || "Unknown API error"
          );
          toast.error(
            result.message ||
              "Failed to submit your search criteria. Please try again."
          );
        }
      } catch (error) {
        console.error("❌ Error during new criteria submission:", error);
        toast.error(
          "Network error during criteria submission. Please try again later."
        );
      }
    }
    // Case 2: EDIT MODE (Single PUT call for comprehensive update)
    else {
      // Re-construct PropertyAddresses from selectedLocations for the PUT payload
      const propertyAddressesForPut = selectedLocations.map((location) => {
        if (location.type === "zip-range" && location.originalData) {
          const {
            countryId,
            countryISO,
            countryName,
            zipFrom,
            zipTo,
            city,
            zip,
          } = location.originalData || {};
          return {
            countryId: countryId, // Assuming this is static for Switzerland, confirm with backend
            countryISO: countryISO,
            city: "", // Empty for zip ranges
            zipCode: "", // Empty for zip ranges
            fullAddress: `Zip Range: ${zipFrom} - ${zipTo} (${countryName})`,
            // IMPORTANT: The PUT API schema you shared for PropertyAddresses does NOT include `zipList`.
            // If backend needs it for update, this needs to be clarified.
            // For now, I'm including it as it was in your GET response, but it might be ignored.
            zipList: {
              zipRange: [zipFrom, zipTo],
            },
          };
        } else if (location.type === "map-area" && location.originalData) {
          const {
            countryId,
            countryISO,
            country: countryName,
            city,
            zip: zipCode,
          } = location.originalData; // Note: 'country' is mapped to 'countryName', 'zip' to 'zipCode'
          return {
            countryId: countryId, // Dynamically use fetched countryId
            countryISO: countryISO, // Dynamically use fetched countryISO
            city: city,
            zipCode: zipCode,
            fullAddress: `${city}, ${zipCode}, ${countryName}`,
            zipList: {
              // Included, but confirm backend expectation
              [city]: [zipCode],
            },
          };
        }
        return {}; // Fallback, should not happen with validated selectedLocations
      });

      // Construct the FULL payload for PUT /api/property/{id}
      const fullUpdatePayload = {
        offeringType: formData.offeringType,
        propertyType: formData.propertyType,
        price: Number(removeApostrophes(formData.price)) || 0,
        bedrooms: Number(formData.bedrooms) || 0,
        units: Number(formData.units) || 0,
        index: formData.index ? Number(formData.index) : null,
        squareMeters: Number(removeApostrophes(formData.squareFootage)) || 0,
        listType: "demand", // Crucial to indicate demand type
        PropertyAddresses: propertyAddressesForPut,
        // PropertyCriteria and PropertyShowcases are for 'offer' properties.
        // As per your instruction, we DO NOT send these for demand flow.
        DemandPropertyCriteria: {
          // FIXED: Backend expects "DemandPropertyCriteria" (plural), not "DemandPropertyCriterium"
          mustCriteria: transformedMustCriteria,
          shouldCriteria: transformedShouldCriteria,
          matchingLevel: apiMatchingLevel,
        },
      };

      console.log(
        `📦 Sending full property update payload (EDIT MODE) to API: PUT ${BASE_URL}/property/${propertyId}`
      );
      console.log("📦 Full payload:", fullUpdatePayload);
      console.log("📦 DemandPropertyCriteria in payload:", fullUpdatePayload.DemandPropertyCriteria);
      console.log("📦 Must criteria:", fullUpdatePayload.DemandPropertyCriteria?.mustCriteria);
      console.log("📦 Should criteria:", fullUpdatePayload.DemandPropertyCriteria?.shouldCriteria);

      try {
        const response = await authFetch(`${BASE_URL}/property/${propertyId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(fullUpdatePayload),
        });
        const result = await response.json();

        if (response.ok) {
          console.log(
            "✅ Property and Demand criteria updated successfully:",
            result
          );

          // Trigger matching recalculation after successful property update
          try {
            const matchingResponse = await authFetch(
              `${BASE_URL}/property/matching/recalculate`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  propertyId: propertyId,
                }),
              }
            );
            if (matchingResponse.ok) {
              console.log(
                "✅ Matching algorithm triggered successfully for demand property"
              );
            } else {
              console.warn(
                "⚠️ Matching recalculation request failed, but property was updated"
              );
            }
          } catch (matchingError) {
            // Don't fail the whole operation if matching trigger fails
            console.warn(
              "⚠️ Error triggering matching recalculation:",
              matchingError
            );
          }

          toast.success("Your demand property has been successfully updated!");
          // Clean up
          navigate(
            isAuthenticated && currentUser && currentUser.userType === "demand"
              ? "/"
              : "/auth/login?flowType=demand"
          );
        } else {
          console.error(
            "❌ API error updating property:",
            result.message || "Unknown API error"
          );
          toast.error(
            result.message || "Failed to update property. Please try again."
          );
        }
      } catch (error) {
        console.error("❌ Error during full property update:", error);
        toast.error("Network error during update. Please try again later.");
      }
    }
  };
  function normalizeValueToKey(value) {
    return value
      .replace(/[^a-zA-Z0-9]+/g, "_") // Replace one or more non-alphanumeric chars with a single underscore
      .replace(/^_|_$/g, "") // Remove leading/trailing underscores
      .toLowerCase();
  }

  const bedroomOptions = [
    { label: "1", value: "1" },
    { label: "2", value: "2" },
    { label: "3", value: "3" },
    { label: "4", value: "4" },
    { label: "5", value: "5" },
    { label: "6+", value: "6" },
  ];

  // Debug: Log the visibility condition for left panel
  console.log("🔍 Left Panel Visibility Check (SearchProperty):", {
    isLoading,
    hasError: !!error,
    demandsCount: demands.length,
    shouldShow: !isLoading && !error && demands.length > 0,
  });

  return (
    <div className={styles.container}>
      {/* Only show left panel if there's data */}

      <div className={styles.leftPanel}>
        {!isLoading && !error && demands.length > 0 && (
          <div className={styles.newSearchesHeader}>
            <h2 className={styles.newSearchesTitle}>{t("new_demands")}</h2>
          </div>
        )}
        {demands.length > 0 && (
          <div className={styles.propertyList}>
            <NewOffersSwiper items={demands} className={styles.mySwiper} />
          </div>
        )}
      </div>

      <div className={styles.rightPanel}>
        <div className={styles.tabContainer}>
          <button
            className={`${styles.tabButton} ${
              activeTab === 1 ? styles.activeTab : ""
            }`}
            style={{
              borderTopLeftRadius: "15px",
              opacity: maxAllowedStep < 1 ? 0.6 : 1,
              cursor: maxAllowedStep < 1 ? "not-allowed" : "pointer",
            }}
            onClick={() => {
              if (maxAllowedStep >= 1) {
                setStep(1);
                setActiveTab(1);
              }
            }}
            disabled={maxAllowedStep < 1}
          >
            {t("Looking-for-Property")}
          </button>
          <button
            className={`${styles.tabButton} ${
              activeTab === 2 ? styles.activeTab : ""
            }`}
            style={{
              opacity: maxAllowedStep < 2 ? 0.6 : 1,
              cursor: maxAllowedStep < 2 ? "not-allowed" : "pointer",
            }}
            onClick={() => {
              if (maxAllowedStep >= 2) {
                setStep(2);
                setActiveTab(2);
              }
            }}
            disabled={maxAllowedStep < 2}
          >
            {t("Must_Criteria")}
            {t("Max_5")}
          </button>
          <button
            className={`${styles.tabButton} ${
              activeTab === 3 ? styles.activeTab : ""
            }`}
            style={{
              borderTopRightRadius: "10px",
              opacity: maxAllowedStep < 3 ? 0.6 : 1,
              cursor: maxAllowedStep < 3 ? "not-allowed" : "pointer",
            }}
            onClick={() => {
              if (maxAllowedStep >= 3) {
                setStep(3);
                setActiveTab(3);
              }
            }}
            disabled={maxAllowedStep < 3}
          >
            {t("Nice_To_Have")} (Max-10)
          </button>
        </div>
        {step === 1 && (
          <>
            <h3 className={styles.sectionHeader}>{t("property_type")}</h3>
            <div
              className={styles.radioGroup}
              style={{ flexDirection: "row", gap: "10px", flexWrap: "wrap" }}
            >
              {["House/Villa", "Apartment", "Multifamily", "Plot"]
                .filter((typeDisplayString) => {
                  if (
                    formData.offeringType === "rental" &&
                    (typeDisplayString === "Multifamily" ||
                      typeDisplayString === "Plot")
                  ) {
                    return false;
                  }
                  return true;
                })
                .map((typeDisplayString) => {
                  const jsonKey = displayStringToKeyMap[typeDisplayString];

                  if (!jsonKey) {
                    console.warn(
                      `Translation key missing for display string: "${typeDisplayString}". Please ensure it's mapped in 'displayStringToKeyMap'.`
                    );
                    return null;
                  }

                  const isChecked = formData.propertyType === jsonKey;

                  return (
                    <label key={jsonKey} className={styles.radioLabel}>
                      <input
                        type="radio"
                        name="propertyType"
                        checked={isChecked}
                        onChange={() =>
                          handleRadioChange("propertyType", jsonKey)
                        }
                        className={styles.radioInput}
                      />
                      <span className={styles.radioCustom}>
                        {isChecked && <span className={styles.radioDot}></span>}
                      </span>
                      {t(jsonKey)}
                    </label>
                  );
                })}
            </div>

            <h3 className={styles.sectionHeader}>{t("title-demand")}..</h3>
            <div
              className={styles.radioGroup}
              style={{ flexDirection: "row", gap: "26px" }}
            >
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  name="offeringType"
                  checked={formData.offeringType === "purchase"}
                  onChange={() => handleRadioChange("offeringType", "purchase")}
                  className={styles.radioInput}
                />
                <span className={styles.radioCustom}>
                  {formData.offeringType === "purchase" && (
                    <span className={styles.radioDot}></span>
                  )}
                </span>
                {t("purchase")}
              </label>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  name="offeringType"
                  checked={formData.offeringType === "rental"}
                  onChange={() => handleRadioChange("offeringType", "rental")}
                  className={styles.radioInput}
                />
                <span className={styles.radioCustom}>
                  {formData.offeringType === "rental" && (
                    <span className={styles.radioDot}></span>
                  )}
                </span>
                {t("rent")}
              </label>
            </div>

            <div className={styles.addressHeaderContainer}>
              <h3>{t("address")} </h3>
              {selectedLocations.length > 0 && (
                <button
                  className={styles.addLocationButton}
                  onClick={handleDrawMapClick}
                >
                  +
                </button>
              )}
            </div>
            {selectedLocations.length > 0 && (
              <div className={styles.selectedTagContainer}>
                <div className={styles.tagsWrapper}>
                  {selectedLocations.map((loc, index) => (
                    <div key={index} className={styles.selectedTag}>
                      {loc.originalData?.zip
                        ? `${loc.display} (${loc.originalData.zip})`
                        : loc.display}
                      <button
                        className={styles.tagCloseButton}
                        onClick={() => handleDeleteLocation(index, loc.type)}
                      >
                        ✖
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedLocations.length === 0 && (
              <div className={styles.addressContainer}>
                <div
                  className={styles.drawMapButton}
                  onClick={handleDrawMapClick}
                >
                  <span className={styles.drawMapLeft}>
                    <span className={styles.drawMapIcon}>🗺️</span>
                    <span className={styles.drawMapText}>
                      <span className={styles.drawMapLabel}>{t("Draw_Area_on_Map")}</span>
                      <span className={styles.drawMapHint}>{t("select_location")}</span>
                    </span>
                  </span>
                  <span className={styles.drawMapArrow}>▼</span>
                </div>
              </div>
            )}

            <h3 className={styles.sectionHeader}>
              {formData.offeringType === "rental"
                ? t("max_price_chf_monthly")
                : t("max_price_chf")}
            </h3>
            <input
              type="text"
              name="price"
              value={formData.price}
              onChange={handleChange}
              className={styles.textInput}
              placeholder={
                formData.offeringType === "rental"
                  ? `${t("max_price_chf_monthly")} *`
                  : `${t("max_price_chf")} *`
              }
            />

            <div className={styles.bedroomsSquareFootageRow}>
              {formData.propertyType !== "plot" &&
                formData.propertyType !== "multifamily" && (
                  <div className={styles.bedroomsInputContainer}>
                    <h3 className={styles.sectionHeader}>
                      {t("no_of_bedrooms")}
                    </h3>
                    <select
                      name="bedrooms"
                      value={formData.bedrooms}
                      onChange={handleChange}
                      className={`${styles.textInput} ${
                        !formData.bedrooms ? styles.placeholderSelect : ""
                      }`}
                    >
                      <option value="" disabled>
                        {`${t("Select bedrooms")} *`}
                      </option>
                      {bedroomOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              {formData.propertyType === "multifamily" && (
                <div className={styles.unitsInputContainer}>
                  <h3 className={styles.sectionHeader}>{t("Unit")}</h3>
                  <input
                    type="text"
                    name="units"
                    value={formData.units}
                    onChange={handleChange}
                    className={styles.textInput1}
                    placeholder={t("Units")}
                  />
                </div>
              )}
              {formData.propertyType === "plot" && (
                <div className={styles.indexInputContainer}>
                  <h3 className={styles.sectionHeader}>{t("Index")}</h3>
                  <input
                    type="number"
                    step="0.01"
                    name="index"
                    value={formData.index}
                    onChange={handleChange}
                    className={styles.textInput1}
                    placeholder={t("Index")}
                  />
                </div>
              )}
            </div>
            <div className={styles.squareFootageInputContainer}>
              <h3 className={styles.sectionHeader}>{t("sqft")}</h3>
              <input
                type="text"
                name="squareFootage"
                value={formData.squareFootage}
                onChange={handleChange}
                className={styles.textInput}
                placeholder={`${t("sqft")} *`}
              />
            </div>

            <div className={styles.navigation}>
              <button
                type="button"
                onClick={handleNextStep}
                className={styles.nextButton}
              >
                {t("next")}
              </button>
            </div>
          </>
        )}
        {step === 2 && (
          <>
            <div className={styles.criteriaGrid2}>
              {loadingCriteria ? (
                <div>{t("Loading criteria...")}</div>
              ) : (
                (() => {
                  const totalMustCount = calculateTotalSelected(formData.mustCriteria);
                  console.log("🔍 Step 2 - Must Criteria Debug:", {
                    totalMustCount,
                    mustCriteria: formData.mustCriteria,
                    criteriaKeys: Object.keys(formData.mustCriteria || {}),
                    criteriaValues: Object.values(formData.mustCriteria || {})
                  });
                  return null;
                })(),
                criteriaGroups
                  ?.filter((group) => group.groupName !== "default_criteria")
                  ?.map((group, groupIndex) => {
                    const mustSelectedOptions = ensureArray(
                      formData.mustCriteria[group.groupName]
                    );
                    const totalMustSelected = calculateTotalSelected(
                      formData.mustCriteria
                    );

                    // DEBUG: Log for first group to see data structure
                    if (groupIndex === 0 && isEditMode) {
                      console.log("🔍 CHECKBOX DEBUG - First Group:");
                      console.log("  - Group name:", group.groupName);
                      console.log("  - Group values (available options):", group.values);
                      console.log("  - formData.mustCriteria for this group:", formData.mustCriteria[group.groupName]);
                      console.log("  - mustSelectedOptions (ensured array):", mustSelectedOptions);
                      console.log("  - Type of mustSelectedOptions:", typeof mustSelectedOptions, Array.isArray(mustSelectedOptions));
                      console.log("  - First option from group.values:", group.values[0]);
                      console.log("  - Does mustSelectedOptions include first option?", mustSelectedOptions.includes(group.values[0]));
                    }

                    return (
                      <React.Fragment key={groupIndex}>
                        <div className={styles.criteriaRowBorder}></div>
                        <div className={styles.criteriaRowContainer}>
                          {group.values.map((option, optionIndex) => {
                            // Extract both display value and identifier from option
                            const optionValue = getDisplayValue(option);
                            const optionIdentifier = getIdentifier(option);

                            // Use identifier-based comparison to handle both old and new formats
                            const isChecked = isCriteriaSelected(mustSelectedOptions, option);
                            const isDisabled =
                              !isChecked && totalMustSelected >= 5;

                            // DEBUG: Log checkbox state for first few options
                            if (groupIndex === 0 && optionIndex < 3 && isEditMode) {
                              console.log(`  📋 Option "${optionValue}": checked=${isChecked}, type=${typeof option}`);
                            }

                            const translationKeyPart =
                              normalizeValueToKey(optionValue);
                            const fullTranslationKey = `${group.groupName}.${translationKeyPart}`;

                            return (
                              <label
                                key={optionIndex}
                                className={styles.radioLabel}
                                style={{
                                  ...(isDisabled
                                    ? { opacity: 0.6, cursor: "not-allowed" }
                                    : {}),
                                }}
                              >
                                <input
                                  type="checkbox"
                                  name={group.groupName}
                                  checked={isChecked}
                                  onChange={() =>
                                    handleCheckboxChange(
                                      group.groupName,
                                      optionIdentifier
                                    )
                                  }
                                  disabled={isDisabled}
                                  className={styles.radioInput}
                                />
                                <span className={styles.radioCustom}>
                                  {isChecked && (
                                    <span className={styles.radioDot2}>
                                      <span className={styles.tickMark}></span>
                                    </span>
                                  )}
                                </span>
                                {t(fullTranslationKey) !== fullTranslationKey
                                  ? t(fullTranslationKey)
                                  : optionValue}
                              </label>
                            );
                          })}
                        </div>
                      </React.Fragment>
                    );
                  })
              )}
              <div className={styles.criteriaRowBorder}></div>
            </div>

            {/* Navigation Buttons */}
            <div className={styles.navigation}>
              <button
                type="button"
                onClick={prevStep}
                className={`${styles.button} ${styles.backButton}`}
              >
                {t("BACK")}
              </button>
              <button
                type="button"
                onClick={handleNextFromMustCriteria}
                className={`${styles.button} ${styles.nextButton2}`}
                style={{ marginLeft: '335px' }}
              >
                {t("next")}
              </button>
            </div>
          </>
        )}
        {step === 3 && (
          <>
            <div className={styles.matchingPreferencesContainer}>
              {/* Left Side – Nice to Have Criteria */}
              <div className={styles.criteriaColumn2}>
                <div className={styles.criteriaGrid2}>
                  {loadingCriteria ? (
                    <div>{t("Loading criteria...")}</div>
                  ) : (
                    (() => {
                      const totalNiceCount = calculateTotalSelected(formData.shouldCriteria);
                      console.log("🔍 Step 3 - Should Criteria Debug:", {
                        totalNiceCount,
                        totalNiceSelectedMemo: totalNiceSelected,
                        shouldCriteria: formData.shouldCriteria,
                        criteriaKeys: Object.keys(formData.shouldCriteria || {}),
                        criteriaValues: Object.values(formData.shouldCriteria || {})
                      });
                      return null;
                    })(),
                    criteriaGroups
                      ?.filter(
                        (group) => group.groupName !== "default_criteria"
                      )
                      ?.map((group, groupIndex) => {
                        const shouldSelectedOptions = ensureArray(
                          formData.shouldCriteria[group.groupName]
                        );
                        const totalNiceSelectedInForm = totalNiceSelected;

                        // DEBUG: Log for first group to see data structure
                        if (groupIndex === 0 && isEditMode) {
                          console.log("🔍 SHOULD CHECKBOX DEBUG - First Group:");
                          console.log("  - Group name:", group.groupName);
                          console.log("  - Group values (available options):", group.values);
                          console.log("  - formData.shouldCriteria for this group:", formData.shouldCriteria[group.groupName]);
                          console.log("  - shouldSelectedOptions (ensured array):", shouldSelectedOptions);
                          console.log("  - Type of shouldSelectedOptions:", typeof shouldSelectedOptions, Array.isArray(shouldSelectedOptions));
                        }

                        return (
                          <React.Fragment key={groupIndex}>
                            <div className={styles.criteriaRowBorder}></div>
                            <div className={styles.criteriaRowContainer}>
                              {group.values.map((option, optionIndex) => {
                                // Extract both display value and identifier from option
                                const optionValue = getDisplayValue(option);
                                const optionIdentifier = getIdentifier(option);

                                // Use identifier-based comparison to handle both old and new formats
                                const isMustHave = isCriteriaSelected(
                                  formData.mustCriteria[group.groupName] || [],
                                  option
                                );
                                const isChecked = isCriteriaSelected(shouldSelectedOptions, option);

                                // DEBUG: Log checkbox state for first few options
                                if (groupIndex === 0 && optionIndex < 3 && isEditMode) {
                                  console.log(`  📋 Should Option "${optionValue}": checked=${isChecked}, isMustHave=${isMustHave}`);
                                }

                                const isDisabled =
                                  (!isChecked &&
                                    totalNiceSelectedInForm >= 10) ||
                                  isMustHave;

                                const translationKeyPart =
                                  normalizeValueToKey(optionValue);
                                const fullTranslationKey = `${group.groupName}.${translationKeyPart}`;

                                return (
                                  <label
                                    key={optionIndex}
                                    className={styles.radioLabel}
                                    style={{
                                      ...(isDisabled
                                        ? {
                                            opacity: 0.6,
                                            cursor: "not-allowed",
                                          }
                                        : {}),
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      name={group.groupName}
                                      checked={isChecked}
                                      onChange={() =>
                                        handleCheckboxChangeShould(
                                          group.groupName,
                                          optionIdentifier
                                        )
                                      }
                                      disabled={isDisabled}
                                      className={styles.radioInput}
                                    />
                                    <span className={styles.radioCustom}>
                                      {isMustHave ? (
                                        <span
                                          style={{
                                            position: "absolute",
                                            left: "50%",
                                            top: "50%",
                                            transform: "translate(-50%, -50%)",
                                            height: "16px",
                                            width: "16px",
                                            borderRadius: "50%",
                                            border:
                                              "2px solid rgba(41, 177, 133, 0.6)",
                                            background:
                                              "rgba(41, 177, 133, 0.2)",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                          }}
                                        >
                                          <span
                                            className={styles.tickMark2}
                                          ></span>
                                        </span>
                                      ) : (
                                        isChecked && (
                                          <span className={styles.radioDot2}>
                                            <span
                                              className={styles.tickMark}
                                            ></span>
                                          </span>
                                        )
                                      )}
                                    </span>
                                    {t(fullTranslationKey) !==
                                    fullTranslationKey
                                      ? t(fullTranslationKey)
                                      : optionValue}
                                  </label>
                                );
                              })}
                            </div>
                          </React.Fragment>
                        );
                      })
                  )}
                  <div className={styles.criteriaRowBorder}></div>
                </div>
              </div>
              {/* Minimum Matching Level section hidden for Search User */}
              {/* <div className={styles.matchingLevelColumn}>
                <h2 className={styles.matchingLevelTitle}>
                  {t("Minimum_Matching_Level")}
                </h2>

                {/* Matching Percentage Options */}
              {/* <div className={styles.matchingOptionsRow}>
                  {matchingLevelOptions.percentage.map((option) => (
                    <label key={option.id} className={styles.radioLabel2}>
                      <input
                        type="radio"
                        name="matchingPercentage"
                        checked={
                          formData.matchingLevel.percentage === option.value
                        }
                        onChange={() =>
                          handleMatchingLevelRadioChange(
                            "percentage",
                            option.value
                          )
                        }
                        className={styles.radioInput}
                      />
                      <span className={styles.radioCustom}>
                        {formData.matchingLevel.percentage === option.value && (
                          <span className={styles.radioDot}></span>
                        )}
                      </span>
                      {option.label}
                    </label>
                  ))}
                </div> */}

              {/* Matching Type Options (Source Type) */}
              {/* <div className={styles.matchingOptionsRow}>
                  {matchingLevelOptions.source_type.map((option) => (
                    <label key={option.id} className={styles.radioLabel2}>
                      <input
                        type="radio"
                        name="matchingSourceType"
                        checked={
                          formData.matchingLevel.source_type === option.value
                        }
                        onChange={() =>
                          handleMatchingLevelRadioChange(
                            "source_type",
                            option.value
                          )
                        }
                        className={styles.radioInput}
                      />
                      <span className={styles.radioCustom}>
                        {formData.matchingLevel.source_type ===
                          option.value && (
                          <span className={styles.radioDot}></span>
                        )}
                      </span>
                      {option.label}
                    </label>
                  ))}
                </div>
              </div> */}
            </div>

            {/* Navigation Buttons for Step 3 */}
            <div className={styles.navigation}>
              <button
                type="button"
                onClick={prevStep}
                className={`${styles.button} ${styles.backButton}`}
              >
                {t("BACK")}
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className={`${styles.button} ${styles.publishButton}`}
                style={{ marginLeft: '335px' }}
              >
                {t("Confirm_Submission")}
              </button>
            </div>
          </>
        )}

        {showMapModal && (
          <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
              <button
                className={styles.closeButton}
                onClick={() => setShowMapModal(false)}
              >
                ✖
              </button>
              <GoogleMapDrawCircle
                onDone={(newAreas) => {
                  console.log("Extracted areas from shape:", newAreas);
                  setFormData((prev) => ({
                    ...prev,
                    areasFromShape: [...prev.areasFromShape, ...newAreas],
                  }));

                  const newLocationTags = newAreas.map((area) => ({
                    display: area.city || `Drawn Area (${area.zip})`,
                    type: "map-area",
                    originalData: area,
                  }));
                  setSelectedLocations((prev) => [...prev, ...newLocationTags]);

                  setShowMapModal(false);
                  setIsAddingNewLocation(false);
                  toast.success(t("Area(s) drawn successfully!"));
                }}
                onClose={() => setShowMapModal(false)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchProperty;
